/**
 * @module hooks/useAnalyzeSubmit
 * @description
 *   `POST /api/upload` submission flow for the analyze form: validation,
 *   credit pre-check/re-sync, simulated upload/analysis progress, response
 *   handling (credits, subscription, daily-analysis counters), and error
 *   classification/toasts.
 *
 * @extracted-from client/src/App.js (lines ~397-705, Phase 7)
 *
 * @invariants
 *   - POST /api/upload request fields unchanged (audioFile/bookFile, craftType, clientSubmissionCount, clientPaidAnalyses, etc.)
 *   - Progress: real upload 0–30%; analysis eases asymptotically toward ~92% (~70s);
 *     snaps to 100% when the response arrives (no artificial stall on early completion)
 *   - localStorage keys: criticai_submission_count, criticai_paid_analyses, criticai_last_daily_analysis, criticai_daily_analyses_available
 *   - Error → toast copy for 413/402/403/timeout/network unchanged
 *
 * @dependencies
 *   - api/client, utils/analytics, utils/getTotalAnalysesRemainingParts, react-hot-toast
 */

import api from '../api/client';
import analytics from '../utils/analytics';
import getTotalAnalysesRemainingParts from '../utils/getTotalAnalysesRemainingParts';
import { toast } from 'react-hot-toast';

/** Real upload maps into this band; analysis sim starts after. */
const PROGRESS_UPLOAD_CAP = 30;
/** Soft ceiling while waiting on the server — leave headroom for “Finalizing…”. */
const PROGRESS_ANALYSIS_ASYMPTOTE = 92;
/**
 * Time constant for 1 − e^(−t/τ). At ~70s the bar sits in the high-80s,
 * then creeps slowly instead of slamming into 99% early.
 */
const PROGRESS_ANALYSIS_TAU_MS = 25500;

function simulatedAnalysisProgress(elapsedMs) {
  const gap = PROGRESS_ANALYSIS_ASYMPTOTE - PROGRESS_UPLOAD_CAP;
  const value =
    PROGRESS_ANALYSIS_ASYMPTOTE - gap * Math.exp(-elapsedMs / PROGRESS_ANALYSIS_TAU_MS);
  return Math.round(Math.min(PROGRESS_ANALYSIS_ASYMPTOTE, Math.max(PROGRESS_UPLOAD_CAP, value)));
}

/**
 * @param {Object} options
 * @param {'music'|'book'} options.craftType
 * @param {Object} options.formData
 * @param {(updater: Object | ((prev: Object) => Object)) => void} options.setFormData
 * @param {File|null} options.selectedFile
 * @param {string} options.error — current form error (used to detect a retry attempt for analytics)
 * @param {(msg: string) => void} options.setError
 * @param {(loading: boolean) => void} options.setIsLoading
 * @param {(pct: number) => void} options.setUploadProgress
 * @param {(results: Object|null) => void} options.setResults
 * @param {number} options.totalAnalysesRemaining
 * @param {number} options.paidAnalyses
 * @param {(n: number) => void} options.setPaidAnalyses
 * @param {number} [options.paidAnalysesHighWater]
 * @param {(n: number) => void} [options.setPaidAnalysesHighWater]
 * @param {number} options.submissionCount
 * @param {(n: number) => void} options.setSubmissionCount
 * @param {boolean} options.hasActiveSubscription
 * @param {(active: boolean) => void} options.setHasActiveSubscription
 * @param {number} options.subscriptionAnalysesUsed
 * @param {(n: number) => void} options.setSubscriptionAnalysesUsed
 * @param {number} options.subscriptionAnalysesLimit
 * @param {(n: number) => void} options.setSubscriptionAnalysesLimit
 * @param {(v: string|null) => void} options.setLastDailyAnalysis
 * @param {(n: number) => void} options.setDailyAnalysesAvailable
 * @param {number} options.dailyAnalysesAvailable
 * @param {() => Promise<Object|null>} options.syncSessionState
 * @param {(message?: string, variant?: 'info'|'error') => void} options.scrollToPricingSection
 * @param {() => string[]} options.validateForm
 * @returns {{ handleSubmit: (e: Event) => Promise<void> }}
 */
export function useAnalyzeSubmit(options) {
  const {
    craftType,
    formData,
    setFormData,
    selectedFile,
    error,
    setError,
    setIsLoading,
    setUploadProgress,
    setResults,
    totalAnalysesRemaining,
    paidAnalyses,
    setPaidAnalyses,
    paidAnalysesHighWater = 0,
    setPaidAnalysesHighWater,
    submissionCount,
    setSubmissionCount,
    hasActiveSubscription,
    setHasActiveSubscription,
    subscriptionAnalysesUsed,
    setSubscriptionAnalysesUsed,
    subscriptionAnalysesLimit,
    setSubscriptionAnalysesLimit,
    setLastDailyAnalysis,
    setDailyAnalysesAvailable,
    dailyAnalysesAvailable,
    syncSessionState,
    scrollToPricingSection,
    validateForm,
  } = options;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isRetryAttempt = Boolean(error);

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      toast.error(validationErrors[0]);
      return;
    }

    // Check credits. Re-sync first when UI shows 0 — mobile Safari often loses the
    // cross-origin session cookie after Stripe; localStorage may still hold credits.
    let remainingForSubmit = totalAnalysesRemaining;
    let paidForSubmit = paidAnalyses;
    if (remainingForSubmit <= 0) {
      try {
        const synced = await syncSessionState();
        if (synced) {
          remainingForSubmit = getTotalAnalysesRemainingParts(synced);
          paidForSubmit = synced.paidAnalyses;
          if (synced.paidAnalyses > paidAnalyses) {
            setPaidAnalyses(synced.paidAnalyses);
          }
          if (
            setPaidAnalysesHighWater &&
            typeof synced.paidAnalysesHighWater === 'number'
          ) {
            setPaidAnalysesHighWater(
              Math.max(synced.paidAnalysesHighWater, synced.paidAnalyses || 0)
            );
          }
          if (synced.hasActiveSubscription && !hasActiveSubscription) {
            setHasActiveSubscription(true);
            setSubscriptionAnalysesUsed(synced.subscriptionAnalysesUsed);
            setSubscriptionAnalysesLimit(synced.subscriptionAnalysesLimit);
          }
        }
      } catch (syncErr) {
        console.error('Pre-submit session sync failed:', syncErr);
      }
    }

    if (remainingForSubmit <= 0) {
      analytics.trackCreditsExhausted('analysis_submit_blocked');
      scrollToPricingSection(
        "You're out of analyses. Purchase credits or subscribe below, then return to run another review.",
        'error'
      );
      return;
    }

    setIsLoading(true);
    setError('');
    setResults(null);
    setUploadProgress(0);

    // For book analysis, ensure form data is fresh
    if (craftType === 'book') {
      // Clear any cached form data to ensure fresh analysis
      setFormData(prev => ({
        ...prev,
        manuscript: prev.manuscript.trim() // Ensure clean manuscript text
      }));

      // Force a complete reset of the form state
      setTimeout(() => {
        setFormData(prev => ({
          ...prev,
          bookTitle: prev.bookTitle.trim(),
          authorName: prev.authorName.trim(),
          genre: prev.genre.trim(),
          targetAudience: prev.targetAudience.trim()
        }));
      }, 50);
    }

    // Track analysis start
    analytics.trackAnalyzeClick(craftType);
    if (isRetryAttempt) {
      analytics.trackAnalysisRetried(craftType);
    }
    if (craftType === 'music') {
      analytics.trackAnalysisStart(formData.trackTitle, formData.artistName);
      analytics.trackFileUpload(selectedFile.type || selectedFile.name.split('.').pop(), selectedFile.size);
    } else {
      analytics.trackAnalysisStart(formData.bookTitle, formData.authorName);
      analytics.trackFileUpload(selectedFile.type || selectedFile.name.split('.').pop(), selectedFile.size);

      // Only clear cache if we're starting a completely new analysis
      // This allows intelligent caching to work on the backend
      setResults(null);
      setError('');
      // Ensure form data is clean for the analysis
      setFormData(prev => ({
        ...prev,
        manuscript: prev.manuscript.trim()
      }));
    }

    const formDataToSend = new FormData();

    if (craftType === 'music') {
      formDataToSend.append('audioFile', selectedFile);
      formDataToSend.append('trackTitle', formData.trackTitle.trim());
      formDataToSend.append('artistName', formData.artistName.trim());
      formDataToSend.append('lyrics', formData.lyrics.trim());
      formDataToSend.append('isInstrumental', formData.isInstrumental ? 'true' : 'false');
      formDataToSend.append('genre', formData.genre.trim());
      if (formData.subgenre && formData.subgenre.trim()) {
        formDataToSend.append('subgenre', formData.subgenre.trim());
      }
      formDataToSend.append('craftType', 'music');
    } else {
      formDataToSend.append('bookFile', selectedFile);
      formDataToSend.append('bookTitle', formData.bookTitle.trim());
      formDataToSend.append('authorName', formData.authorName.trim());
      formDataToSend.append('manuscript', formData.manuscript.trim());
      formDataToSend.append('genre', formData.genre.trim());
      formDataToSend.append('targetAudience', formData.targetAudience.trim());
      formDataToSend.append('craftType', 'book');
      // Add multiple cache-busting parameters for book analysis to ensure fresh results
      formDataToSend.append('timestamp', Date.now().toString());
      formDataToSend.append('sessionId', Math.random().toString(36).substring(7));
      formDataToSend.append('requestId', `${Date.now()}-${Math.random().toString(36).substring(7)}`);
    }

    formDataToSend.append('clientSubmissionCount', submissionCount.toString());
    formDataToSend.append('clientPaidAnalyses', paidForSubmit.toString());

    // Progress: real upload → 0–30%; then ease toward ~92% while analyzing.
    // Completing early jumps straight to 100% (no forced wait at the top).
    let uploadComplete = false;
    let analysisStartTime = null;
    let sawUploadProgress = false;

    const markUploadComplete = () => {
      if (uploadComplete) return;
      uploadComplete = true;
      analysisStartTime = Date.now();
      setUploadProgress((prev) => Math.max(prev, PROGRESS_UPLOAD_CAP));
    };

    const progressInterval = setInterval(() => {
      if (!uploadComplete || analysisStartTime == null) return;
      setUploadProgress(simulatedAnalysisProgress(Date.now() - analysisStartTime));
    }, 200);

    // Only if the browser never reports multipart progress (common on tiny bodies).
    const uploadFailsafe = setTimeout(() => {
      if (!uploadComplete && !sawUploadProgress) {
        markUploadComplete();
      }
    }, 1500);

    try {
      // No need to clear cache before API call - intelligent caching handles it

      const response = await api.post('/api/upload', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          sawUploadProgress = true;
          const total = progressEvent.total || 0;
          if (!total) {
            // Loaded bytes without total — nudge into analysis once bytes have moved.
            if (progressEvent.loaded > 0) {
              clearTimeout(uploadFailsafe);
              markUploadComplete();
            }
            return;
          }
          // Map 0–100% upload into 0–30 display band as whole percents only
          // (percentCompleted * 0.3 alone yields decimals like 11.1%).
          const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
          if (percentCompleted < 100) {
            setUploadProgress(
              Math.min(PROGRESS_UPLOAD_CAP, Math.round(percentCompleted * 0.3))
            );
          } else {
            clearTimeout(uploadFailsafe);
            markUploadComplete();
          }
        },
      });

      // Response arrived — finish immediately even if the sim is only at 50%.
      clearTimeout(uploadFailsafe);
      clearInterval(progressInterval);
      setUploadProgress(100);

      setResults(response.data);

      // Update subscription analyses from response if available
      if (response.data.subscriptionAnalysesUsed !== undefined) {
        setSubscriptionAnalysesUsed(response.data.subscriptionAnalysesUsed);
      }
      if (response.data.subscriptionAnalysesLimit !== undefined) {
        setSubscriptionAnalysesLimit(response.data.subscriptionAnalysesLimit);
      }
      // Check subscription status - prioritize explicit hasActiveSubscription flag
      if (response.data.hasActiveSubscription === true ||
          response.data.subscriptionStatus === 'active' ||
          (response.data.subscription && response.data.subscription.status === 'active')) {
        setHasActiveSubscription(true);
      } else {
        setHasActiveSubscription(false);
      }

      // Refresh subscription status when results are displayed
      syncSessionState();

      // Log intelligent caching information for book analysis
      if (craftType === 'book') {
        console.log('Book analysis completed:', {
          timestamp: response.data.analysisTimestamp,
          manuscriptHash: response.data.manuscriptHash,
          cacheUsed: response.data.cacheUsed,
          requestId: response.data.requestId,
          sessionId: response.data.sessionId,
          clientTimestamp: response.data.timestamp
        });

        if (response.data.cacheUsed) {
          console.log('📚 Using cached results for identical manuscript');
        } else {
          console.log('🆕 Generated fresh analysis for new manuscript');
        }
      }

      // Update submission count or paid analyses based on response
      if (response.data.submissionCount !== undefined) {
        setSubmissionCount(response.data.submissionCount);
        localStorage.setItem('criticai_submission_count', response.data.submissionCount.toString());
      }

      // Update paid analyses if provided
      if (response.data.paidAnalyses !== undefined) {
        setPaidAnalyses(response.data.paidAnalyses);
        localStorage.setItem('criticai_paid_analyses', response.data.paidAnalyses.toString());
      }

      const nextPaid =
        response.data.paidAnalyses !== undefined ? response.data.paidAnalyses : paidAnalyses;
      const nextHwRaw =
        typeof response.data.paidAnalysesHighWater === 'number'
          ? response.data.paidAnalysesHighWater
          : paidAnalysesHighWater;
      const nextHw = Math.max(nextHwRaw || 0, nextPaid || 0);
      if (setPaidAnalysesHighWater) {
        setPaidAnalysesHighWater(nextHw);
      }
      localStorage.setItem('criticai_paid_analyses_high_water', nextHw.toString());

      // Update daily analysis information if provided
      if (response.data.lastDailyAnalysis !== undefined) {
        setLastDailyAnalysis(response.data.lastDailyAnalysis);
        localStorage.setItem('criticai_last_daily_analysis', response.data.lastDailyAnalysis);
      }

      if (response.data.dailyAnalysesAvailable !== undefined) {
        setDailyAnalysesAvailable(response.data.dailyAnalysesAvailable);
        localStorage.setItem('criticai_daily_analyses_available', response.data.dailyAnalysesAvailable.toString());
      }

      // Check if user has now run out of analyses after this submission
      // (Daily analysis feature is disabled)
      const updatedSubmissionCount =
        response.data.submissionCount !== undefined ? response.data.submissionCount : submissionCount;
      const updatedPaidAnalyses =
        response.data.paidAnalyses !== undefined ? response.data.paidAnalyses : paidAnalyses;
      const updatedHasSub =
        response.data.hasActiveSubscription !== undefined
          ? response.data.hasActiveSubscription === true
          : hasActiveSubscription;
      const updatedSubUsed =
        response.data.subscriptionAnalysesUsed !== undefined
          ? response.data.subscriptionAnalysesUsed
          : subscriptionAnalysesUsed;
      const updatedSubLimit =
        response.data.subscriptionAnalysesLimit !== undefined
          ? response.data.subscriptionAnalysesLimit
          : subscriptionAnalysesLimit;
      const updatedDaily =
        response.data.dailyAnalysesAvailable !== undefined
          ? response.data.dailyAnalysesAvailable
          : dailyAnalysesAvailable;

      const remainingAfterRun = getTotalAnalysesRemainingParts({
        hasActiveSubscription: updatedHasSub,
        subscriptionAnalysesLimit: updatedSubLimit,
        subscriptionAnalysesUsed: updatedSubUsed,
        paidAnalyses: updatedPaidAnalyses,
        submissionCount: updatedSubmissionCount,
        dailyAnalysesAvailable: updatedDaily,
      });

      if (remainingAfterRun <= 0) {
        setTimeout(() => {
          scrollToPricingSection(
            'To keep analyzing tracks and books, choose a plan below — you will be redirected to Stripe to pay securely.',
            'info'
          );
        }, 8000);
      }

      // Track successful analysis completion
      const hasContent = craftType === 'music' ? !!formData.lyrics : !!formData.manuscript;
      analytics.trackAnalysisComplete(response.data.scores, hasContent);

      // Show success toast
      toast.success('Analysis completed successfully!');

    } catch (err) {
      clearTimeout(uploadFailsafe);
      clearInterval(progressInterval);

      console.error('API Error:', err);

      let errorType = 'general_error';
      let errorMessage = 'An error occurred during analysis. Please try again.';

      if (err.response?.status === 413) {
        errorType = 'file_too_large';
        errorMessage = 'File size too large. Please upload a file smaller than 25MB.';
      } else if (err.response?.status === 402) {
        errorType = 'payment_required';
        errorMessage = 'Payment required. You have used your free analyses.';
        analytics.trackCreditsExhausted('payment_required_402');
        scrollToPricingSection(errorMessage, 'error');
      } else if (err.response?.status === 403) {
        errorType = 'forbidden_error';
        errorMessage = err.response?.data?.message || err.response?.data?.error || 'Access denied. Please check your connection or contact support.';
        console.error('403 Forbidden error:', err.response?.data);
      } else if (err.response?.data?.error) {
        errorType = 'api_error';
        errorMessage = err.response.data.error;
      } else if (err.code === 'ECONNABORTED') {
        errorType = 'timeout_error';
        errorMessage = 'Request timed out. Please try again with a smaller file.';
      } else if (err.message.includes('Network Error') || err.message.includes('CORS')) {
        errorType = 'network_error';
        errorMessage = 'Network error. Please check your connection and try again. If the problem persists, it may be a CORS configuration issue.';
      }

      setError(errorMessage);
      analytics.trackAnalysisError(errorType, errorMessage);
      toast.error(errorMessage);
    } finally {
      clearTimeout(uploadFailsafe);
      clearInterval(progressInterval);
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  return { handleSubmit };
}

export default useAnalyzeSubmit;
